/**
 * Project / user helpers for team leads, activity, and account status.
 */

/**
 * Resolve team lead UIDs for a project from `teamLeaders[]`.
 *
 * @param {Object|null|undefined} project
 * @returns {string[]}
 */
export function getProjectTeamLeaders(project) {
    if (!project || !Array.isArray(project.teamLeaders)) return [];
    return project.teamLeaders.filter(
        (uid) => typeof uid === "string" && uid.trim() !== ""
    );
}

/**
 * Whether the user is a team lead on the project.
 *
 * @param {Object|null|undefined} project
 * @param {string} userId
 * @returns {boolean}
 */
export function isProjectTeamLead(project, userId) {
    if (!userId) return false;
    return getProjectTeamLeaders(project).includes(userId);
}

/**
 * Projects are active unless explicitly marked inactive.
 *
 * @param {Object|null|undefined} project
 * @returns {boolean}
 */
export function isProjectActive(project) {
    if (!project) return false;
    return project.isActive !== false;
}

/**
 * Sort comparator: active projects first, inactive last.
 * Use as the primary key before name/order comparisons.
 *
 * @param {Object|null|undefined} a
 * @param {Object|null|undefined} b
 * @returns {number}
 */
export function compareActiveProjectsFirst(a, b) {
    const aInactive = a && a.isActive === false ? 1 : 0;
    const bInactive = b && b.isActive === false ? 1 : 0;
    return aInactive - bInactive;
}

/**
 * Display label for a project. Inactive projects append " (Inactive)".
 *
 * @param {Object|string|null|undefined} projectOrName - Project object or name string
 * @param {boolean} [isActive] - Optional override when passing a name string
 * @returns {string}
 */
export function formatProjectLabel(projectOrName, isActive) {
    const name =
        typeof projectOrName === "string"
            ? projectOrName
            : projectOrName?.name ||
              projectOrName?.project ||
              projectOrName?.projectName ||
              "";
    const active =
        typeof isActive === "boolean"
            ? isActive
            : typeof projectOrName === "object"
            ? isProjectActive(projectOrName)
            : true;
    if (!name) return active ? "" : "(Inactive)";
    return active ? name : `${name} (Inactive)`;
}

/**
 * Users are active unless accountStatus is "inactive".
 *
 * @param {Object|null|undefined} user
 * @returns {boolean}
 */
export function isUserActive(user) {
    if (!user) return false;
    return (user.accountStatus || "active") !== "inactive";
}

/**
 * Whether the viewer may open ProjectInfo for a project.
 * Admins (viewProjects includes "all") can access everything; others only managed projects.
 *
 * @param {string} projectId
 * @param {Object|null|undefined} rights
 * @param {Array<Object>} managedProjects
 * @returns {boolean}
 */
export function canAccessProject(projectId, rights, managedProjects = []) {
    if (!projectId) return false;
    if (rights?.viewProjects?.includes("all")) return true;
    return (managedProjects || []).some((p) => p.projectId === projectId);
}

/**
 * Normalize teamLeaders for persistence (1–2 unique UIDs).
 *
 * @param {string[]} teamLeaders
 * @returns {{ teamLeaders: string[] }}
 */
export function normalizeTeamLeadersForSave(teamLeaders = []) {
    const unique = [];
    (teamLeaders || []).forEach((uid) => {
        if (typeof uid === "string" && uid.trim() && !unique.includes(uid)) {
            unique.push(uid.trim());
        }
    });
    return {
        teamLeaders: unique.slice(0, 2),
    };
}

/**
 * Projects where the user is the only team lead.
 * Only active projects are considered (inactive ones must not block demote).
 *
 * @param {Array<Object>} projects
 * @param {string} userId
 * @returns {Array<Object>}
 */
export function getSoleTeamLeadProjects(projects = [], userId) {
    if (!userId) return [];
    return (projects || []).filter((project) => {
        if (!isProjectActive(project)) return false;
        const leads = getProjectTeamLeaders(project);
        return leads.length === 1 && leads[0] === userId;
    });
}

/**
 * Parent-project select options: top-level projects only (no parent of their
 * own), excluding `excludeProjectId`, sorted A–Z by name.
 *
 * @param {Array<Object>} projects
 * @param {string} [excludeProjectId]
 * @returns {Array<{ title: string, value: string }>}
 */
export function getParentProjectOptions(projects = [], excludeProjectId = "") {
    return (projects || [])
        .filter((p) => {
            if (!p?.projectId) return false;
            if (excludeProjectId && p.projectId === excludeProjectId) {
                return false;
            }
            return !p.parentProjectId;
        })
        .map((p) => ({
            title: p.name || "Unnamed Project",
            value: p.projectId,
        }))
        .sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Project ids that are parents of at least one other project.
 *
 * @param {Array<Object>} projects
 * @returns {Set<string>}
 */
export function getParentIdsWithChildren(projects = []) {
    const parents = new Set();
    (projects || []).forEach((project) => {
        if (project?.parentProjectId) {
            parents.add(project.parentProjectId);
        }
    });
    return parents;
}

/**
 * Odoo ids that need Pulse project names in export descriptions:
 * parent/child umbrella groups, plus legacy duplicate odooId strings.
 * (Parent-only rows still omit the name in export — Odoo already shows it.)
 *
 * @param {Array<Object>} projects
 * @returns {Set<string>}
 */
export function getUmbrellaOdooIds(projects = []) {
    const odooIdOwners = {};
    const parentsWithChildren = getParentIdsWithChildren(projects);
    const umbrella = new Set();

    (projects || []).forEach((project) => {
        if (!project?.projectId) return;

        const odooId =
            project.odooId === null || project.odooId === undefined
                ? ""
                : String(project.odooId).trim();
        if (odooId) {
            if (!odooIdOwners[odooId]) odooIdOwners[odooId] = new Set();
            odooIdOwners[odooId].add(project.projectId);
        }
    });

    (projects || []).forEach((project) => {
        if (!project?.projectId) return;
        const odooId =
            project.odooId === null || project.odooId === undefined
                ? ""
                : String(project.odooId).trim();
        if (!odooId) return;
        if (
            project.parentProjectId ||
            parentsWithChildren.has(project.projectId)
        ) {
            umbrella.add(odooId);
        }
    });

    Object.keys(odooIdOwners).forEach((id) => {
        if (odooIdOwners[id].size > 1) umbrella.add(id);
    });

    return umbrella;
}
